import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { merge, Subscription, timer } from 'rxjs';
import { filter, flatMap } from 'rxjs/operators';
import { BusinessTransactionWorkQueueDetailModel } from '../../shared/models/business-transaction/business-transaction-work-queue-detail-model';
import { UserLoginModel } from '../../shared/models/business-transaction/user-login.model';
import { WorkQueueModel } from '../../shared/models/business-transaction/work-queue.model';
import { ValidationModel } from '../../shared/models/validation-results/validation.model';
import { AppSettings } from '../../shared/settings/app-settings.model';
import { TransactionPrequalWindowComponent } from '../prequal/transaction-prequal-modal.component';
import { OpenTransactionData, OpenTransactionWindow } from './modals/open-transaction-modal.component';
import { TransactionDeleteWindow, TransactionDeleteWindowContext } from './modals/transaction-delete-modal.component';
import { UnassignActiveWindow } from './modals/unassign-active-modal.component';
import { TransactionQueueDelegate } from './transaction-queue-delegate.class';

export class AssignmentDropdown {
    businessTransaction: BusinessTransactionWorkQueueDetailModel;
    recipientUserLoginId: number;
}

export class NotifyRequest {
    public actionString: string;
    public transactions: any[];
}

@Component({
    selector: 'transaction-queue',
    templateUrl: './transaction-queue.html',
    providers: [ TransactionQueueDelegate ]
})
export class TransactionQueueComponent implements OnDestroy {
    WORKQUEUETYPE_HELPQUEUE = 2;
    public csrOverridesQueue: BusinessTransactionWorkQueueDetailModel[];
    public levelOneQueue: any[];
    public masterDropdownList: any[] = [];
    public personalQueueData: BusinessTransactionWorkQueueDetailModel[];
    public queueData: WorkQueueModel[] = [];
    public buQueueSelected: boolean[] = [];
    public escQueueSelected = false;
    public personalQueueSelected = true;
    public personalEscalationsQueueSelected = false;
    public showAccordion: boolean;
    public showBuQueue = false;
    public showCsrOverridesQueue: boolean;
    public showEscQueue = false;
    public showLevelOneQueue: boolean;
    public showPersonalQueue = false;
    public multiSortMeta = [];
    private queueDataSize: number;
    private csrOverridesQueueSubscription: Subscription;
    private levelOneQueueSubscription: Subscription;
    private masterDropdownListSubscription: Subscription;
    private personalQueueDataSubscription: Subscription;
    private timerSubscription: Subscription;

    @ViewChild('table') table: Table;

    constructor(
        private appSettings: AppSettings,
        public delegate: TransactionQueueDelegate
    ) {}

    ngOnInit() {
        this.masterDropdownListSubscription = this.delegate.masterDropdownList$
            .subscribe(results => this.masterDropdownList = results.slice(0));

        this.personalQueueDataSubscription = this.delegate.personalQueueData$
            .subscribe(results => {
                this.personalQueueData = results.slice(0);
                this.queueData = this.delegate.tmpQueueData.slice(0);
                this.resetBuQueueSelected(this.queueData.length);
                this.showPersonalQueue = true;
                this.showAccordion = true;
            });

        this.csrOverridesQueueSubscription = this.delegate.csrOverridesQueue$
            .subscribe(results => {
                this.csrOverridesQueue = results.slice(0);
                this.showCsrOverridesQueue = results.length > 0;
            });

        this.levelOneQueueSubscription = this.delegate.levelOneQueue$
            .subscribe(results => {
                this.levelOneQueue = results.slice(0);
                this.showLevelOneQueue = true;
            });

        this.timerSubscription = timer(0, this.appSettings.screenRefresh.queueRefreshInterval)
            .subscribe(() => this.delegate.loadQueueData());
    }

    ngOnDestroy() {
        this.csrOverridesQueueSubscription.unsubscribe();
        this.levelOneQueueSubscription.unsubscribe();
        this.masterDropdownListSubscription.unsubscribe();
        this.personalQueueDataSubscription.unsubscribe();
        this.timerSubscription.unsubscribe();
        this.showAccordion = false;
        this.showPersonalQueue = false;
        this.queueData = undefined;
        this.delegate.ngOnDestroy();
    }

    get businessUnitEscalationsQueueSelected(): boolean {
        return this.escQueueSelected;
    }

    set businessUnitEscalationsQueueSelected(escQueueSelected: boolean) {
        this.escQueueSelected = escQueueSelected;
        this.showEscQueue = escQueueSelected ? escQueueSelected : this.showEscQueue;

        if (escQueueSelected) {
            this.delegate.loadQueueData();
        }
    }

    deleteTransaction(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        const transactionNumber = businessTransaction.transactionId;

        var data = new TransactionDeleteWindowContext(transactionNumber);
        const deleteModal$ = this.delegate.modalService.open(TransactionDeleteWindow, data).afterClosed();
        const accept$ = deleteModal$.pipe(
            filter(result => !!result),
            flatMap(() => this.delegate.businessTransactionService.expireBusinessTransactionForUser(transactionNumber, this.delegate.userId)),
            flatMap(() => this.delegate.cartService.removeCartItem(+transactionNumber)),
        );
        const cancel$ = deleteModal$.pipe(filter(result => !result));
        merge(accept$, cancel$)
            .subscribe(
                () => this.delegate.loadQueueData(),
                err => this.handleValidationResult(err)
            );
    }

    escalationResponse(response: any) {
        if (response) {
            this.handleValidationResult(response);
        }
        this.delegate.loadQueueData();
    }

    escalationToggled(event) {
        this.businessUnitEscalationsQueueSelected = event;
    }

    findUser(userLoginId: number): UserLoginModel {
        for (const user of this.masterDropdownList) {
            if (user.id === userLoginId) {
                return user;
            }
        }
        return null;
    }

    handleChildAssignmentDropDown(assignment: AssignmentDropdown) {
        const agentNumber = assignment.businessTransaction.agentNumber;
        // Assigning only selected transaction to user instead of assigning all to avoid active transaction assignment assignment issue.
        // for more detais Bug #122566
        const transactionId = assignment.businessTransaction.transactionId;
        const agentResults = this.getTransactionByTransactionId(transactionId, this.queueData);
        this.handleAssignmentDropdownOperation(assignment.businessTransaction, assignment.recipientUserLoginId, agentResults);
    }

    handleValidationResult(error) {
        this.delegate.validationResults = new ValidationModel(error);
        this.delegate.showValidationComponent = true;
    }

    holdTransaction(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        const agentNumber = businessTransaction.agentNumber;
        if (!!agentNumber) {
            const unassignedTransactionGroup = this.getUnassignedTransactionIdsForAgent(agentNumber, this.queueData);
            const transactionNumber = businessTransaction.transactionId;
            const isTransactionAssigned: boolean = businessTransaction.assignedUserLoginId !== null;
            // if transaction is assigned, return individual transaction to WIP; otherwise return all unassigned transactions for agent to WIP
            const transactionArray = isTransactionAssigned ? [transactionNumber] : unassignedTransactionGroup;
            this.returnTransactionToWip(transactionArray);
        }
    }

    notifyRequester(notifyRequest: NotifyRequest) {
        let startOfTomorrow = new Date();
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
        startOfTomorrow.setHours(0, 0, 0, 0);
        notifyRequest.transactions.forEach(businessTransaction => {
            this.delegate.systemNotificationService.dispatchSystemNotification(
                [1],
                [+businessTransaction.overrideRequestedById],
                `${this.delegate.user.name} ${notifyRequest.actionString} your transaction.`, null,
                startOfTomorrow
            ).subscribe((x) => { });
        });
    }

    onTabChange(event: any) {
        const index = +event.index;
        if (this.isIndexInQueue(index, this.buQueueSelected)) {
            this.businessUnitQueueSelected(index);
        }
    }

    async redirectToMyTransaction(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        const data = await this.prepareOpenTransactionData(businessTransaction);
        this.openTransactionModal(data);
    }

    unassignTransaction(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        const transactionNumber = businessTransaction.transactionId;
        const status = businessTransaction.statusTypeId;

        if (status !== this.delegate.WIP_STATUS_ACTIVE) {
            this.delegate.businessTransactionService.returnBusinessTransactionToQueue(transactionNumber)
                .subscribe(
                    () => this.delegate.loadQueueData(),
                    err => this.handleValidationResult(err));
        } else {
            this.delegate.modalService.open(UnassignActiveWindow).afterClosed()
                .pipe(filter(result => !!result))
                .subscribe(() => {
                    this.delegate.businessTransactionService.returnBusinessTransactionToQueue(transactionNumber)
                        .subscribe(
                            () => this.delegate.loadQueueData(),
                            err => this.handleValidationResult(err));
                });
        }
    }

    private businessUnitQueueSelected(index: number) {
        this.buQueueSelected[index] = !this.buQueueSelected[index];
        this.showBuQueue = this.buQueueSelected.some(val => val) ? true : this.showBuQueue;

        if (this.buQueueSelected[index]) {
            this.delegate.loadQueueData();
        }
    }

    private completed(result: OpenTransactionData) {
        this.delegate.showValidationComponent = false;
        // Launch the Pre-qual Modal
        if (result && result.businessTransactionNumberId) {
            this.openPrequalModal(result);
        }
    }

    private getTransactionsForAgent(agentNumber: number, queueData: WorkQueueModel[]) {
        const queuesWithTransactions = queueData.filter(queue => queue.businessTransactionDetails.length > 0);
        const queueTransactions = queuesWithTransactions.map(queue => queue.businessTransactionDetails);
        const flatTransactions = [].concat(...queueTransactions) as BusinessTransactionWorkQueueDetailModel[];
        return flatTransactions.filter(trans => trans.agentNumber === agentNumber);
    }

    private getTransactionByTransactionId(transactionId: string, queueData: WorkQueueModel[]) {
        const queuesWithTransactions = queueData.filter(queue => queue.businessTransactionDetails.length > 0);
        const queueTransactions = queuesWithTransactions.map(queue => queue.businessTransactionDetails);
        const flatTransactions = [].concat(...queueTransactions) as BusinessTransactionWorkQueueDetailModel[];
        const assignTransactions = flatTransactions.filter(trans => trans.transactionId === transactionId);

        const packagedObj = assignTransactions.reduce((acc, val) => ({
            agentTransactionIds: [...acc.agentTransactionIds, val.transactionId],
            agentTransactionStatus: [...acc.agentTransactionStatus, val.status]
        }), { agentTransactionIds: [], agentTransactionStatus: [] });

        return [packagedObj.agentTransactionIds, packagedObj.agentTransactionStatus];
    }

    private getTransactionGroup(agentNumber: number, queueData: WorkQueueModel[]) {
        const transactionsForAgent = this.getTransactionsForAgent(agentNumber, queueData);
        const packagedObj = transactionsForAgent.reduce((acc, val) => ({
            agentTransactionIds: [...acc.agentTransactionIds, val.transactionId],
            agentTransactionStatus: [...acc.agentTransactionStatus, val.status]
        }), { agentTransactionIds: [], agentTransactionStatus: [] });

        return [packagedObj.agentTransactionIds, packagedObj.agentTransactionStatus];
    }

    private getUnassignedTransactionIdsForAgent(agentNumber: number, queueData: WorkQueueModel[]): any[] {
        const transactionsForAgent = this.getTransactionsForAgent(agentNumber, queueData);
        const unassignedTransactionsForAgent = transactionsForAgent.filter(trans => trans.assignedTo.trim() === '');

        return unassignedTransactionsForAgent.map(trans => trans.transactionId);
    }

    private handleAssignmentDropdownOperation(businessTransaction: BusinessTransactionWorkQueueDetailModel, recipientUserLoginId: number, agentResults: any[]) {
        const originalUserLoginId = businessTransaction.assignedUserLoginId;
        const status = businessTransaction.statusTypeId;

        // only reassign to different login ID
        if (recipientUserLoginId !== originalUserLoginId) {
            const isTransactionAssigned = originalUserLoginId !== null;
            const transactionNumber = isTransactionAssigned ? businessTransaction.transactionId : agentResults[0];
            const recipient = this.findUser(+recipientUserLoginId);
            const originalUser = this.findUser(+originalUserLoginId);

            this.delegate.performAssignment(transactionNumber, status, recipient, originalUser, this.delegate.userId)
                .subscribe(
                    () => this.delegate.loadQueueData(),
                    err => this.handleValidationResult(err)
                );
        }
    }

    private isIndexInQueue(index: number, queue: any[]) {
        return queue.length > 0 && index >= 0 && index < queue.length;
    }

    private openPrequalModal(result) {
        this.delegate.modalService.open(TransactionPrequalWindowComponent, result);
    }

    private openTransactionModal(data: OpenTransactionData) {
        if (data.openToEditMode) {
            this.completed(data);
        } else {
            this.delegate.modalService.open(OpenTransactionWindow, data).afterClosed()
                .subscribe(result => this.completed(result));
        }
    }

    private async prepareOpenTransactionData(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        const transactionId = businessTransaction.transactionId;
        const agentNumber = businessTransaction.agentNumber;
        const recipientUserLoginId = this.delegate.userId;
        const updatedMetadata = await this.delegate.getTransactionMetaData(transactionId);
        const originalUserLoginId = +updatedMetadata.assignedUserId;
        const status = updatedMetadata.wipStatusTypeId;
        const openToEditMode = (recipientUserLoginId === originalUserLoginId);
        const originator = this.findUser(+originalUserLoginId);
        const recipient = this.findUser(+recipientUserLoginId);
        return new OpenTransactionData(transactionId, status, originator, recipient, this.delegate.userId, agentNumber, openToEditMode);
    }

    private resetBuQueueSelected(length: number) {
        if (this.queueDataSize !== length) {
            this.queueDataSize = length;
            this.buQueueSelected = Array.from(Array(length), () => false);
        }
    }

    private returnTransactionToWip(transactionArray: any[]) {
        this.delegate.businessTransactionService.returnTransactionToWip(transactionArray)
            .subscribe(
                () => this.delegate.loadQueueData(),
                err => this.handleValidationResult(err));
    }
}
